import {
  type VendorCatalogBrowseResult,
  type VendorCatalogPrice,
  type VendorCatalogProduct,
} from '@choco-admin/types';

import {
  type SuppliersPageVendorCatalogResponseFragment,
  type SuppliersPageVendorCatalogUnitFragment,
} from '../../../../../../../generated';

const DEFAULT_ERROR_MESSAGE = 'Failed to load catalog products.';

/**
 * Whether the operator has typed a search term. Empty / whitespace-only means
 * the default browse (`vendorItemsForVendor`); anything else switches to
 * full-text search (`vendorItemUnitsForVendorSearch`).
 */
export const isCatalogSearchMode = (searchTerm: string): boolean =>
  searchTerm.trim().length > 0;

const mapPrice = (
  unit: SuppliersPageVendorCatalogUnitFragment
): VendorCatalogPrice | null => {
  const money = unit.price?.displayCatalogPrice ?? unit.price?.displayPrice;

  if (!money) {
    return null;
  }

  return {
    amount: money.amount,
    currency: money.currency,
    decimalPlaces: money.decimalPlaces,
  };
};

const mapProduct = (
  unit: SuppliersPageVendorCatalogUnitFragment
): VendorCatalogProduct => ({
  id: unit.id,
  // The "Product" is the parent VendorItem's name (e.g. "Paper"); a vendor may
  // override it per item. `unit.name` (e.g. "Ream", "Pack") is the packaging
  // UNIT, shown in the Unit column — not the product name.
  name: unit.itemNameOverride ?? unit.vendorItem?.node?.name ?? unit.name,
  productCode: unit.productCode ?? null,
  unit: unit.name,
  price: mapPrice(unit),
  imageUrl: unit.images?.[0]?.asset?.url?.url ?? null,
  stockStatus: unit.stockStatus,
  // `isOrdering` is NOT availability — it only says the unit is purchasable
  // rather than a base-unit reference. The Status chip combines it with
  // `stockStatus`; see `resolveCatalogStatusChip`.
  isEnabled: unit.isOrdering,
});

/**
 * Maps a `VendorItemsForVendorResponse` union into a domain result. Returns
 * `null` when there is no response yet (still loading / skipped). The failure
 * union members are surfaced as `{ kind: 'error' }`.
 */
export const mapVendorCatalogResponse = (
  response: SuppliersPageVendorCatalogResponseFragment | null | undefined
): VendorCatalogBrowseResult | null => {
  if (!response) {
    return null;
  }

  switch (response.__typename) {
    case 'VendorItemsForVendorResult':
      return {
        kind: 'success',
        page: {
          products: (response.edges ?? []).map((edge) => mapProduct(edge.node)),
          totalCount: response.totalCount ?? 0,
          pageInfo: {
            hasNextPage: response.pageInfo?.hasNextPage ?? false,
            endCursor: response.pageInfo?.endCursor ?? '',
          },
        },
      };
    case 'InputValidationFailure':
    case 'AuthorizationFailure':
      return {
        kind: 'error',
        message: response.message ?? DEFAULT_ERROR_MESSAGE,
      };
    default:
      return { kind: 'error', message: DEFAULT_ERROR_MESSAGE };
  }
};

/**
 * Formats a `VendorCatalogPrice` (minor-unit `MoneyV2`) for display, e.g.
 * `$12.34`. Falls back to `<amount> <currency>` for unknown currency codes and
 * to an em dash when there is no price.
 */
export const formatVendorCatalogPrice = (
  price: VendorCatalogPrice | null
): string => {
  if (!price) {
    return '—';
  }

  const value = price.amount / 10 ** price.decimalPlaces;

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: price.currency,
      minimumFractionDigits: price.decimalPlaces,
      maximumFractionDigits: price.decimalPlaces,
    }).format(value);
  } catch {
    return `${value.toFixed(price.decimalPlaces)} ${price.currency}`;
  }
};
